import { ComponentProps } from "react";
import { QueryBody } from "@api/internal/utils/types";
import Card from "@particles/card/Card";
import TemplateG from "@particles/card/templates/TemplateG/TemplateG";
import { getTemplateGProps } from "@particles/card/templates/TemplateG/utils/helpers";
import TemplateH from "@particles/card/templates/TemplateH/TemplateH";
import { CardVariations } from "@particles/card/utils/types";
import { useListingLoadMoreStories } from "@particles/listing/hooks/useListingLoadMoreStories";

type ListingProps = {
  /** The template props to use for the articles. */
  cardProps: ComponentProps<typeof Card>[];
  /** Whether to use Template H for the first article. */
  isUseTemplateH?: boolean;
  /** Optional Card variant to override default styles for Template H */
  templateHCardVariantOverride?: CardVariations;
  /** Optional Card variant to override default styles for Template G */
  templateGCardVariantOverride?: CardVariations;
} & QueryBody;

/**
 * Stories listing component to display articles in Template H and Template G formats.
 * Figma: https://www.figma.com/design/rmzJoZwUpb3iLm6lMDXJ2W/BT-MASTER--WEB-?node-id=1209-14363&t=m7103yrVxON0jRlB-4
 */
const Listing = ({
  cardProps,
  urlPath,
  sections,
  pageType,
  size,
  isUseTemplateH = false,
  templateHCardVariantOverride,
  templateGCardVariantOverride,
  offset,
}: ListingProps): React.ReactElement => {
  const { ref, loadMoreData } = useListingLoadMoreStories({
    templateProps: getTemplateGProps,
    initialList: cardProps,
    urlPath,
    pageType,
    size,
    sections,
    offset,
  });

  const firstArticle = isUseTemplateH ? cardProps.slice(0, 1) : [];
  const remainingArticles = isUseTemplateH ? cardProps.slice(1) : cardProps;

  return (
    <section data-testid="listing-component">
      {isUseTemplateH ? (
        <TemplateH
          cardProps={firstArticle}
          className="pb-4"
          cardVariantOverride={templateHCardVariantOverride}
        />
      ) : null}

      <TemplateG
        cardProps={[...remainingArticles, ...loadMoreData]}
        className="pt-4"
        cardVariantOverride={templateGCardVariantOverride}
      />

      <div data-testid="listing-load-more-stories-ref" ref={ref} />
    </section>
  );
};

export default Listing;
